import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "src/store";
import { emailRegex } from "src/ui/containers/unauthorized/components/LoginPage/LoginPage";
import { Modal } from "src/ui/shared/Modal/Modal";

import { updateGroupMembers } from "@dashboard/devices/store";
import { GroupRoles } from "@dashboard/devices/types";
import { useUpdateWorkspaceMembersMutation } from "@dashboard/workspaces/api";
import { PrimaryButton } from "@shared/CustomButton";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { TextField } from "@shared/form/TextField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { getAssignableRoleOptions } from "../roles";

const initialValues: FormValues = {
    email: "",
    role: "User",
};

type FormValues = {
    role: GroupRoles;
    email?: string;
};
interface Props {
    id: number;
    name: "group" | "workspace";
    canAssignOwner: boolean;
    open: boolean;
    onClose: VoidFunction;
}

export const AddMemberDialog = (props: Props) => {
    const { t } = useTranslation(["common", "modals"]);
    const dispatch = useDispatch();
    const [updateWorkspaceMembers] = useUpdateWorkspaceMembersMutation();

    const options = React.useMemo(() => getAssignableRoleOptions(props.canAssignOwner), [props.canAssignOwner]);

    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>, form: FormApi<FormValues>) => {
        if (e.key === "Enter" && !form.getState().submitting) {
            await form.submit();
        }
    };
    const validate = (values: FormValues) => {
        const trimmedEmail = values.email?.trim();
        if (!trimmedEmail) {
            return { email: t("modals:addMember.input.email.validation.empty") };
        }
        if (!emailRegex.test(trimmedEmail)) {
            return { email: t("modals:addMember.input.email.validation.invalid") };
        }
        if (!values.role) {
            return { role: t("modals:addMember.input.role.validation.empty") };
        }
    };
    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        const trimmedEmail = values.email?.trim();
        if (props.name === "group" && trimmedEmail) {
            await dispatch(updateGroupMembers({ id: props.id, addedMembers: { [trimmedEmail]: values.role } }))
                .unwrap()
                .then(() => {
                    showSuccessToast(t("modals:addMember.toast.groupMemberAddedSuccess"));
                    form.reset();
                    props.onClose();
                })
                .catch(({ message }) => showErrorToast(message));
        } else if (props.name === "workspace" && trimmedEmail) {
            await updateWorkspaceMembers({ id: props.id, addedMembers: { [trimmedEmail]: values.role } })
                .unwrap()
                .then(() => {
                    showSuccessToast(t("modals:addMember.toast.workspaceMemberAddedSuccess"));
                    form.reset();
                    props.onClose();
                })
                .catch(({ message }) => showErrorToast(message));
        }
    };

    return (
        <Form<FormValues>
            {...{ onSubmit, validate, initialValues }}
            render={({ handleSubmit, submitting, form }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        title={t("modals:addMember.title", { name: props.name })}
                        open={props.open}
                        onClose={props.onClose}
                        onKeyDown={(e) => onKeyDown(e, form)}
                        endButton={
                            <PrimaryButton onClick={form.submit} disabled={submitting} loading={submitting}>
                                {t("common:button.add")}
                            </PrimaryButton>
                        }
                    >
                        <ControlledSelectField
                            name="role"
                            label={t("modals:addMember.input.role.caption")}
                            options={options}
                            disableSorting
                        />
                        <TextField name="email" label={t("modals:addMember.input.email.caption")} required />
                    </Modal>
                </form>
            )}
        />
    );
};
